'use client'

import { Fragment, useState, type ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
  Plane,
  Search,
  Ship,
  Truck,
  UserRound,
} from 'lucide-react'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import type { Consignment } from '@/lib/api/types'
import type { RowSelection } from '@/src/hooks/useRowSelection'
import { SelectAllCheckbox, SelectCheckbox, selectColumnClass } from '@/src/components/BatchActions'
import { RecordCard, RecordCards, RecordFact } from '@/src/components/RecordCard'
import { consignmentLabel } from '@/src/components/QuickEdit'

interface ConsignmentListProps {
  consignments: Consignment[] | null
  /** The server-side search. Without onSearchChange there is no search box. */
  search?: string
  onSearchChange?: (search: string) => void
  /** A search is in flight; the rows shown are the previous answer. */
  searching?: boolean
  /** Shown inside the table's card, under the rows - the page's pagination. */
  footer?: ReactNode
  /** The owner's name for the Owner column; without it the column shows who is unassigned. */
  ownerLabel?: (ownerUserId: string) => string
  /** A control at the end of each row, such as quick edit. Clicking it never expands the row. */
  rowActions?: (consignment: Consignment) => ReactNode
  /** Rows picked for a batch change: a checkbox on each row, and one for all shown. */
  selection?: RowSelection<Consignment>
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleDateString()
}

const MODES = {
  sea: { icon: Ship, label: 'By sea' },
  air: { icon: Plane, label: 'By air' },
  truck: { icon: Truck, label: 'By road' },
} as const

/** How it travels, from its mode or, failing that, its shipping method. */
function consignmentMode(consignment: Consignment) {
  const mode = (consignment.mode || consignment.shipping_method || '').toLowerCase()
  // A charter is worth seeing at a glance: a whole aircraft for one customer.
  if (mode === 'air' && consignment.air_service === 'CHARTER') {
    return { icon: Plane, label: 'Air charter' }
  }
  if (mode === 'sea' || mode === 'air') return MODES[mode]
  if (mode === 'truck' || mode === 'road') return MODES.truck
  return null
}

/** Its own air or sea leg, as small print; a courier's package number never shows here. */
function consignmentTracking(consignment: Consignment) {
  return consignment.international_tracking_number || consignment.tracking_number || null
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function ConsignmentList({
  consignments,
  search = '',
  onSearchChange,
  searching = false,
  footer,
  ownerLabel = (ownerUserId) => ownerUserId,
  rowActions,
  selection,
}: ConsignmentListProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const columns = 8 + (rowActions ? 1 : 0) + (selection ? 1 : 0)

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  // Searching happens on the server, across every page; this only renders.
  const rows = consignments ?? []

  if (rows.length === 0 && !search.trim()) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No consignments yet</h3>
          <p className="text-muted-foreground mb-4">Create your first consignment to get started</p>
          <Link href="/consignments/new">
            <Button>Create Consignment</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {onSearchChange && (
        <div className="relative max-w-md">
          {searching ? (
            <Loader2
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
            />
          ) : (
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
          )}
          <Input
            type="search"
            aria-label="Search consignments"
            aria-busy={searching}
            className="pl-9"
            placeholder="Code, container, B/L, tracking number, customer..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
      )}

      <div
        className={`overflow-hidden rounded-2xl border bg-card text-card-foreground transition-opacity ${searching ? 'opacity-60' : ''}`}
      >
        <div className="relative w-full overflow-auto max-md:hidden">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                {selection && (
                  <th className={`h-10 align-middle ${selectColumnClass(selection)}`}>
                    <SelectAllCheckbox selection={selection} visible={rows} noun="consignment" />
                  </th>
                )}
                <th className="h-10 w-[52px] pr-1 pl-4"></th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Consignment
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Service
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Status
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Owner
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Due
                </th>
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  Created
                </th>
                <th className="h-10 px-3 pr-6 text-right align-middle font-medium text-muted-foreground">
                  Tax Est.
                </th>
                {rowActions && (
                  // Pinned to the right, so a phone reaches it without scrolling the table.
                  <th className="sticky right-0 h-10 w-12 bg-card px-2">
                    <span className="sr-only">Quick edit</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {rows.map((consignment) => (
                <Fragment key={consignment.id}>
                  <tr
                    key={consignment.id}
                    className="group border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted cursor-pointer"
                    onClick={() => toggleRow(consignment.id)}
                    {...selection?.rowProps(consignment, rows)}
                  >
                    {selection && (
                      <td className={`py-2.5 align-middle ${selectColumnClass(selection)}`}>
                        <SelectCheckbox
                          checked={selection.isSelected(consignment.id)}
                          label={`Select ${consignmentLabel(consignment)}`}
                          onToggle={(range) => selection.toggle(consignment, rows, range)}
                        />
                      </td>
                    )}
                    <td className="py-2.5 pr-1 pl-4 align-middle">
                      {expandedRows.has(consignment.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      {/* Named by its title and code; a tracking number is small print, when there is one. */}
                      <div className="max-w-xs truncate font-medium">
                        {consignment.title || consignmentLabel(consignment)}
                      </div>
                      <div className="max-w-xs truncate text-xs text-muted-foreground">
                        {[
                          consignment.code,
                          consignmentTracking(consignment) &&
                            `Tracking ${consignmentTracking(consignment)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      <div className="mt-1 max-w-xs truncate text-xs text-muted-foreground">
                        {consignment.next_action || 'No next action'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary" className="w-fit">
                          {formatLabel(consignment.service_type)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatLabel(consignment.inquiry_stage)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <StatusBadge status={consignment.status} />
                    </td>
                    <td className="px-3 py-2.5 align-middle whitespace-nowrap">
                      {consignment.owner_user_id ? (
                        ownerLabel(consignment.owner_user_id)
                      ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 align-middle whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                        {formatDate(consignment.sla_due_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      {new Date(consignment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2.5 pr-6 align-middle text-right">
                      {consignment.total_tax_estimate
                        ? `UGX ${consignment.total_tax_estimate.toLocaleString()}`
                        : 'N/A'}
                    </td>
                    {rowActions && (
                      <td
                        className="sticky right-0 bg-card px-2 py-1 align-middle group-data-[state=selected]:bg-muted"
                        // The dialog it opens is still inside this row as far as React is concerned.
                        onClick={(event) => event.stopPropagation()}
                      >
                        {rowActions(consignment)}
                      </td>
                    )}
                  </tr>
                  {expandedRows.has(consignment.id) && (
                    <tr className="bg-muted/50 border-b">
                      <td colSpan={columns} className="px-6 py-4 align-middle">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pl-10">
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Mode
                            </span>
                            <span className="capitalize font-medium">{consignment.mode}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Destination
                            </span>
                            <span className="font-medium">{consignment.destination_port}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                              Owner
                            </span>
                            <span className="font-medium">
                              {consignment.owner_user_id
                                ? ownerLabel(consignment.owner_user_id)
                                : 'Unassigned'}
                            </span>
                          </div>
                          <div className="flex items-center justify-end">
                            <Link href={`/consignments/${consignment.id}`}>
                              <Button size="xs" variant="secondary">
                                View Full Details
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns} className="px-6 py-6 text-center text-muted-foreground">
                    No consignments match &quot;{search.trim()}&quot;
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <RecordCards label="Consignments" noun="consignment" rows={rows} selection={selection}>
          {rows.map((consignment) => {
            const mode = consignmentMode(consignment)
            const customer = consignment.customer?.company_name || consignment.customer?.name
            const destination = consignment.destination_port || consignment.destination_address
            const tracking = consignmentTracking(consignment)
            return (
              <RecordCard
                key={consignment.id}
                row={consignment}
                rows={rows}
                href={`/consignments/${consignment.id}`}
                code={consignment.code}
                title={consignment.title || 'Untitled consignment'}
                status={consignment.status}
                selection={selection}
                selectLabel={`Select ${consignmentLabel(consignment)}`}
                details={
                  <>
                    {customer && (
                      <RecordFact className="font-medium text-foreground">{customer}</RecordFact>
                    )}
                    {destination && (
                      <RecordFact icon={mode?.icon} label={mode ? `${mode.label} to` : 'To'}>
                        {destination}
                      </RecordFact>
                    )}
                    <RecordFact icon={UserRound} label="Owner">
                      {consignment.owner_user_id
                        ? ownerLabel(consignment.owner_user_id)
                        : 'Unassigned'}
                    </RecordFact>
                    {consignment.sla_due_at && (
                      <RecordFact icon={CalendarClock} label="Due">
                        {formatDate(consignment.sla_due_at)}
                      </RecordFact>
                    )}
                  </>
                }
                next={consignment.next_action}
                note={
                  tracking && (
                    <p className="truncate text-[0.6875rem] text-muted-foreground/80">
                      Tracking {tracking}
                    </p>
                  )
                }
                action={rowActions?.(consignment)}
              />
            )
          })}
          {rows.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              No consignments match &quot;{search.trim()}&quot;
            </li>
          )}
        </RecordCards>
        {footer}
      </div>
    </div>
  )
}
